import React, { FC, useState, useCallback, useEffect } from 'react';
import { ReactSVG } from 'react-svg';
import { hideDecorativeSvg } from '@/lib/a11y';
import { motion, AnimatePresence } from 'framer-motion';
import cs from 'classnames';
import s from './Header.module.css';
import { HeaderProps } from '@/types/model';
import { SvgLoader } from '@/lib/imageLoader';
import { Menu } from '@/components';
import useIsMobile from '@/hooks/use-is-mobile';
import { Link } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const HEADER_ENTER_IN_SCREEN = 350;

const Header: FC<HeaderProps> = ({ logo, phone, items, social, menuBackground }) => {
  const isMobile = useIsMobile();
  const t = useTranslations('header');
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(() => false);
  const [clientWindowHeight, setClientWindowHeight] = useState<number>(0);

  const menuIsTop = clientWindowHeight <= HEADER_ENTER_IN_SCREEN;

  const handleScroll = useCallback(() => {
    setClientWindowHeight(window.scrollY);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleClickMenu = useCallback(() => {
    setMenuIsOpen(!menuIsOpen);
  }, [menuIsOpen]);

  const header_classnames = cs(s.header, {
    [s.header__scrolled]: !menuIsTop
  });

  const logo_classname = cs(s.header__logo, {
    [s.header__logo__scrolled]: !menuIsTop
  });

  const phone_classname = cs(s.header__phone, {
    [s.header__phone__scrolled]: !menuIsTop
  });

  const menu_classnames = cs(s.header__menu, {
    [s.header__menu__scrolled]: !menuIsTop
  });

  const button_classname = cs(s.header__button, {
    [s.header__button__open]: menuIsOpen
  });

  return (
    <>
      <motion.header
        className={header_classnames}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}>
        <motion.div className={s.header__container}>
          <div className={s.header__container__left}>
            <Link href="/" aria-label={t('goHome')}>
              {logo?.url && (
                <ReactSVG
                  src={SvgLoader({ src: logo?.url })}
                  className={logo_classname}
                  wrapper="span"
                  beforeInjection={hideDecorativeSvg}
                />
              )}
            </Link>
          </div>
          <div className={s.header__container__right}>
            {!isMobile && (
              <div className={phone_classname}>
                <ReactSVG
                  src={SvgLoader({ src: phone.icon?.url })}
                  className={s.phoneicon}
                  wrapper="span"
                  beforeInjection={hideDecorativeSvg}
                />
                <a href={`tel:+${phone.phoneNumber}`}>{phone.phoneNumber}</a>
              </div>
            )}
            <LanguageSwitcher scrolled={!menuIsTop} />
            <motion.button
              onClick={handleClickMenu}
              className={button_classname}
              aria-label={menuIsOpen ? t('closeMenu') : t('openMenu')}>
              <ReactSVG
                wrapper="span"
                src={menuIsOpen ? '/icons/menu-cerrar.svg' : '/icons/menu.svg'}
                className={menu_classnames}
                beforeInjection={hideDecorativeSvg}
              />
            </motion.button>
          </div>
        </motion.div>
      </motion.header>
      <AnimatePresence>
        {menuIsOpen && (
          <Menu
            logo={logo}
            phone={phone}
            items={items}
            social={social}
            menuBackground={menuBackground}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
